import React from 'react';
import { type WnChange, type WnNewUniversity, type WnPlatform, type WnSettings, fmtDay } from './types';

/**
 * "New on Cosmos · Platform updates" (prototype section 1): the last data
 * refresh, the universities that joined in the period, and the IP added /
 * removed at universities already on Cosmos.
 */

interface Props {
  settings: WnSettings;
  platform: WnPlatform;
  onExplore: (u: WnNewUniversity) => void;
  onChange: (c: WnChange) => void;
}

const Eyebrow: React.FC<{ className?: string; children: React.ReactNode }> = ({ className = '', children }) => (
  <span className={`text-[11px] font-semibold uppercase tracking-[0.08em] ${className}`}>{children}</span>
);

const UniversityIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M3 10 12 4l9 6" /><path d="M5 10v8M9.5 10v8M14.5 10v8M19 10v8M3 20h18" />
  </svg>
);

const Empty: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="rounded-xl border border-dashed border-primary-300 bg-primary-100/30 px-5 py-6 text-sm text-grayscale-1000 text-center">
    {children}
  </div>
);

const linkCls = 'text-sm font-medium text-primary-700 hover:text-primary-600 hover:underline underline-offset-2';

export const PlatformUpdates: React.FC<Props> = ({ settings, platform, onExplore, onChange }) => {
  const days = platform.period_days || settings.period_days;
  const lastRun = platform.last_run?.at ? fmtDay(platform.last_run.at, true) : '';
  const showNew = settings.sections.new_universities !== false;
  const showChanges = settings.sections.changes !== false;
  return (
    <section className="flex flex-col gap-5" data-wn-section="platform">
      <div className="flex items-center justify-between flex-wrap gap-3 pb-3.5 border-b border-primary-200">
        <div className="flex items-center gap-3">
          <Eyebrow className="bg-primary-700 text-grayscale-100 px-3 py-1 rounded-full">New on Cosmos</Eyebrow>
          <h2 className="m-0 text-[19px] font-semibold text-grayscale-1300">Platform updates</h2>
        </div>
        {lastRun && (
          <span className="text-[13px] text-grayscale-1000 inline-flex items-center gap-2">
            <span className="w-[7px] h-[7px] rounded-full bg-green-700" aria-hidden="true" />
            Last run {lastRun}
          </span>
        )}
      </div>

      {platform.error && (
        <Empty>Platform updates are unavailable right now. Please try again in a few minutes.</Empty>
      )}

      {showNew && !platform.error && (
        <div className="flex flex-col gap-2.5">
          <h3 className="m-0"><Eyebrow className="text-grayscale-1000">New universities</Eyebrow></h3>
          {platform.new_universities.length === 0 ? (
            <Empty>No new universities in the last {days} days.</Empty>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {platform.new_universities.map(u => (
                <div key={u.code}
                  className="flex items-center gap-4 rounded-xl border border-primary-200 bg-white px-5 py-[18px] shadow-sm">
                  <span className="w-11 h-11 rounded-[10px] bg-primary-100 text-primary-700 flex items-center justify-center flex-none">
                    <UniversityIcon />
                  </span>
                  <div className="flex-1 min-w-0 flex flex-col gap-0.5">
                    <Eyebrow className="text-primary-700">New university</Eyebrow>
                    <span className="text-[17px] font-medium text-grayscale-1300 break-words">{u.name} joined Cosmos</span>
                    <span className="text-[13px] text-grayscale-1000">
                      {[u.country, fmtDay(u.date)].filter(Boolean).join(' · ')}
                    </span>
                  </div>
                  <button type="button" onClick={() => onExplore(u)} className={`${linkCls} flex-none`}>
                    Explore →
                  </button>
                </div>
              ))}
            </div>
          )}
          {platform.new_universities_total > platform.new_universities.length && (
            <span className="text-xs text-grayscale-1000">
              and {platform.new_universities_total - platform.new_universities.length} more in the last {days} days
            </span>
          )}
        </div>
      )}

      {showChanges && !platform.error && (
        <div className="flex flex-col gap-2.5">
          <h3 className="m-0"><Eyebrow className="text-grayscale-1000">Technology changes at existing universities</Eyebrow></h3>
          {platform.changes.length === 0 ? (
            <Empty>No technology changes at existing universities in the last {days} days.</Empty>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
              {platform.changes.map(c => {
                const added = c.kind === 'added';
                return (
                  <div key={c.id}
                    className="flex flex-col gap-1.5 rounded-xl border border-primary-200 bg-white px-[18px] pt-[18px] pb-3.5 shadow-sm">
                    <Eyebrow className={added ? 'text-primary-700' : 'text-red-700'}>
                      {added ? 'IP added' : 'IP removed'}
                    </Eyebrow>
                    <span className="text-lg font-medium text-grayscale-1300 break-words">{c.name}</span>
                    <span className={`text-[17px] font-medium ${added ? 'text-green-700' : 'text-red-700'}`}>
                      {added ? '+' : '−'}{c.n.toLocaleString()} listing{c.n === 1 ? '' : 's'}
                    </span>
                    {c.tags.length > 0 && (
                      <div className="flex gap-1.5 flex-wrap">
                        {c.tags.map(t => (
                          <span key={t} className="text-xs px-2.5 py-0.5 rounded-full bg-primary-100 text-grayscale-1200">{t}</span>
                        ))}
                      </div>
                    )}
                    {!added && (c.titles || []).length > 0 && (
                      <ul className="m-0 pl-4 flex flex-col gap-0.5 text-[13px] text-grayscale-1200 list-disc">
                        {(c.titles || []).map((t, i) => <li key={i} className="break-words">{t}</li>)}
                      </ul>
                    )}
                    <span className="text-[13px] text-grayscale-1000">{fmtDay(c.date, true)}</span>
                    <button type="button" onClick={() => onChange(c)}
                      className="mt-1.5 pt-3 border-t border-primary-200 flex justify-between text-sm font-medium text-primary-700 hover:text-primary-600 text-left">
                      {added ? 'View new tech offers' : c.n > (c.titles || []).length ? `See all ${c.n.toLocaleString()} removed` : 'See removed tech offers'}
                      <span aria-hidden="true">→</span>
                    </button>
                  </div>
                );
              })}
            </div>
          )}
          {platform.changes_total > platform.changes.length && (
            <span className="text-xs text-grayscale-1000">
              and {platform.changes_total - platform.changes.length} more changes in the last {days} days
            </span>
          )}
        </div>
      )}
    </section>
  );
};

export default PlatformUpdates;
